import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFacility } from '../contexts/FacilityContext';
import { useCanMoveMoney, MONEY_ROLE_HINT } from '../contexts/AuthContext';
import { fetchCreditBalances, fetchRecoupments, matchRecoupment, reprocessEraPlb, refundPatientCredit } from '../services/api';
import CreditBalanceModal from '../components/CreditBalanceModal';
import ResolveCreditBalanceModal from '../components/ResolveCreditBalanceModal';
import './Payments.css';
import './CreditBalances.css';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtCurrency(n) {
  return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const STATUS_LABELS = {
  open: 'Open', in_review: 'In Review', pending_refund: 'Pending Refund',
  refund_sent: 'Refund Sent', partially_recouped: 'Partially Recouped',
  recouped: 'Recouped', resolved: 'Resolved', written_off: 'Written Off',
};
const SOURCE_LABELS = {
  rebill_payer_change: 'Payer change', duplicate_payment: 'Duplicate payment',
  overpayment_era: 'ERA overpayment', cob_correction: 'COB correction', manual: 'Manual',
  // ⟳ Step 14 (S14-d)
  patient_overpayment: 'Patient overpayment', unapplied_patient_cash: 'Unapplied cash refund',
};

const TERMINAL_STATUSES = ['resolved', 'recouped', 'refund_sent', 'written_off'];

// ⟳ Step 14 — a patient row's money moves only by a refund, so its partial state reads as one.
const PATIENT_STATUS_LABELS = { ...STATUS_LABELS, partially_recouped: 'Partially Refunded' };
export const OBLIGATION_LABELS = { payer: 'Payer', patient: 'Patient' };
const DUE_BASIS_LABELS = { federal_60_day: 'Federal 60-day', internal_policy: 'Internal policy' };

// S14-k — the Missouri limit on a carrier's refund request. Shown, never acted on, and only
// beside a PAYER row: it says nothing about Medicare, Medicaid or a self-funded plan.
export const CARRIER_LIMIT_LABEL =
  'RSMo §376.384 — state-regulated carriers only (not Medicare, Medicaid or self-funded plans)';
// RSMo 447.539: due diligence for $50 or more, within the year before the report.
const ESCHEAT_DILIGENCE_MIN = 50;

/** A date-only value as the calendar day it names, whatever the viewer's time zone. */
function fmtDay(d) {
  if (!d) return '—';
  const dt = new Date(`${String(d).slice(0, 10)}T12:00:00`);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Whole months from a `YYYY-MM-DD` date to `today` (a Date). */
export function monthsSince(dateStr, today = new Date()) {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
  let months = (today.getFullYear() - y) * 12 + (today.getMonth() + 1 - m);
  if (today.getDate() < d) months -= 1;
  return Math.max(0, months);
}

/**
 * S14-j — the due-diligence window before an escheat report: the year before it, for a
 * still-open patient credit of $50 or more. Null when none applies.
 */
export function dueDiligenceWindow(row) {
  if (row?.obligation_type !== 'patient' || !row.escheat_due_date) return null;
  if (TERMINAL_STATUSES.includes(row.status)) return null;
  if ((parseFloat(row.open_balance) || 0) < ESCHEAT_DILIGENCE_MIN) return null;
  const to = String(row.escheat_due_date).slice(0, 10);
  const from = `${Number(to.slice(0, 4)) - 1}${to.slice(4)}`;
  return { from, to };
}

function agingBucket(days) {
  if (days > 60) return { key: '60+', label: '60+ days', cls: 'ccb-bucket--danger' };
  if (days > 30) return { key: '31-60', label: '31–60 days', cls: 'ccb-bucket--warn' };
  return { key: '0-30', label: '0–30 days', cls: 'ccb-bucket--ok' };
}

export default function CreditBalances() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { facilities } = useFacility();

  const canMove = useCanMoveMoney();
  const [rows, setRows]       = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [statusFilter, setStatusFilter] = useState('unresolved'); // 'unresolved' | 'all'
  const [obligationFilter, setObligationFilter] = useState('all'); // ⟳ Step 14: 'all' | 'payer' | 'patient'
  const [refundTarget, setRefundTarget] = useState(null); // ⟳ Step 14: a patient row being refunded
  const [agingFilter, setAgingFilter]   = useState('all');        // 'all' | '0-30' | '31-60' | '60+'
  const [search, setSearch]             = useState('');
  const [logModal, setLogModal]         = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null); // credit balance being resolved
  const [notice, setNotice]             = useState(null);   // { type: 'success'|'error', message }

  // ERA recoupments (835 PLB) that couldn't be auto-matched to a credit balance.
  const [recoups, setRecoups]       = useState([]);
  const [recoupOpen, setRecoupOpen] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [matchingAdj, setMatchingAdj] = useState(null); // the adjustment awaiting a target credit balance

  const facilityName = (id) => {
    const f = facilities.find((x) => (x.facility_id ?? x.id) === id);
    return f?.name || (id != null ? `Facility ${id}` : '');
  };

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const params = { status: statusFilter };
      if (agingFilter !== 'all') params.aging = agingFilter;
      if (obligationFilter !== 'all') params.obligation_type = obligationFilter;
      const res = await fetchCreditBalances(params);
      setRows(res.data || []);
      setSummary(res.summary || null);
    } catch {
      setError('Failed to load credit balances.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, agingFilter, obligationFilter]);

  useEffect(() => { load(); }, [load]);

  // Deep-link from a claim's credit-balance banner: ?resolve=<id> auto-opens the
  // resolve modal for that credit balance once the worklist has loaded.
  useEffect(() => {
    const resolveId = searchParams.get('resolve');
    if (!resolveId || loading) return;
    const target = rows.find((r) => String(r.credit_balance_id) === resolveId);
    if (target) {
      setResolveTarget(target);
    } else {
      // Not in the current view (e.g. already resolved) — flag it, don't silently no-op.
      setNotice({ type: 'error', message: `Credit balance #${resolveId} isn't in this list — it may already be resolved. Switch to “Include resolved” to view it.` });
    }
    // Consume the param either way so it doesn't reopen on every render.
    searchParams.delete('resolve');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, rows, loading]);

  const loadRecoups = useCallback(async () => {
    try {
      const res = await fetchRecoupments({ matched: 'unmatched' });
      setRecoups(res.data || []);
    } catch { setRecoups([]); }
  }, []);

  useEffect(() => { loadRecoups(); }, [loadRecoups]);

  const handleRescan = async () => {
    setRescanning(true);
    try {
      const res = await reprocessEraPlb();
      await Promise.all([load(), loadRecoups()]);
      setNotice({
        type: 'success',
        message: `Re-scanned stored ERAs. Auto-matched ${res.recoupMatched ?? 0} recoupment(s); `
          + `${res.recoupUnmatched ?? 0} still need manual matching.`,
      });
    } catch (err) {
      setNotice({ type: 'error', message: err?.response?.data?.error || 'Failed to re-scan ERAs for recoupments.' });
    } finally {
      setRescanning(false);
    }
  };

  // Manual match: pick an unmatched recoupment, then click a credit balance to apply it to.
  // The backend caps the applied amount at the credit balance's open amount, so a recoupment
  // larger than the balance only partially clears — surface the leftover so it isn't silently lost.
  const applyMatch = async (creditBalance) => {
    const recoupOutstanding = Number(matchingAdj.amount) - Number(matchingAdj.applied_amount);
    try {
      const res = await matchRecoupment(matchingAdj.provider_adjustment_id, { credit_balance_id: creditBalance.credit_balance_id });
      const applied = Number(res?.applied ?? 0);
      const leftover = recoupOutstanding - applied;
      setMatchingAdj(null);
      await Promise.all([load(), loadRecoups()]);
      setNotice(leftover > 0.005
        ? {
            type: 'success',
            message: `Applied ${fmtCurrency(applied)} to credit balance ${creditBalance.claim_number}. `
              + `${fmtCurrency(leftover)} of this recoupment exceeded its open amount and was NOT applied — `
              + `it stays unmatched so you can apply it to another credit balance.`,
          }
        : { type: 'success', message: `Applied ${fmtCurrency(applied)} to credit balance ${creditBalance.claim_number}.` });
    } catch (err) {
      setNotice({ type: 'error', message: err?.response?.data?.error || 'Failed to match the recoupment.' });
    }
  };

  // Open the resolve modal (amount entry, partial recovery, method picker) for a row.
  const handleResolve = (cb) => setResolveTarget(cb);

  // CMS-838-style Medicare Credit Balance Report worksheet (CSV).
  // Header block identifies the report; detail columns mirror the CMS-838 detail page.
  const exportCsv = () => {
    const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const today = new Date();
    const quarter = `Q${Math.floor(today.getMonth() / 3) + 1} ${today.getFullYear()}`;
    // ⟳ Step 14 (S14-f): the CMS-838 reports money owed to a PAYER. A patient credit is a
    // different liability and never appears on it, whatever the list is filtered to.
    const reportRows = filtered.filter((r) => r.obligation_type !== 'patient');
    const totalOpen = reportRows.reduce((s, r) => s + Number(r.open_balance || 0), 0);

    const meta = [
      ['Medicare Credit Balance Report (CMS-838) — Worksheet'],
      ['Prepared', today.toISOString().slice(0, 10)],
      ['Reporting period', quarter],
      ['Scope', statusFilter === 'unresolved' ? 'Unresolved credit balances' : 'All credit balances'],
      ['Credit balances on report', reportRows.length],
      ['Total outstanding', totalOpen.toFixed(2)],
      [],
    ];

    const header = [
      'Patient Name', 'DOB', 'Member/HIC ID', 'Facility', 'Claim #', 'Type of Bill',
      'Service Date', 'Paid Date', 'ICN/DCN', 'Payer', 'Credit Balance Amount',
      'Amount Repaid', 'Outstanding', 'Reason', 'Date Identified', 'Due Date', 'Status',
    ];
    const detail = reportRows.map((r) => [
      r.patient_name, r.patient_dob?.slice(0, 10) || '', r.member_id || '', facilityName(r.facility_id),
      r.claim_number, r.type_of_bill || '', r.service_date?.slice(0, 10) || '',
      r.paid_date?.slice(0, 10) || '', r.icn || '', r.payer_name || '',
      Number(r.credit_amount).toFixed(2), Number(r.recovered_amount).toFixed(2), Number(r.open_balance).toFixed(2),
      SOURCE_LABELS[r.source] || r.source, r.identified_date?.slice(0, 10) || '',
      r.compliance_due_date?.slice(0, 10) || '', STATUS_LABELS[r.status] || r.status,
    ]);

    const csv = [
      ...meta.map((row) => row.map(q).join(',')),
      header.map(q).join(','),
      ...detail.map((row) => row.map(q).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cms838-credit-balances-${today.toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return r.claim_number?.toLowerCase().includes(q) ||
      r.patient_name?.toLowerCase().includes(q) ||
      r.payer_name?.toLowerCase().includes(q);
  });

  return (
    <div className="payments-page">
      <div className="payments-header">
        <div>
          <h1 className="payments-title">Credit Balances</h1>
          <p className="payments-subtitle">Overpayments owed back to payers and patients — refund, recoupment &amp; escheat tracking</p>
        </div>
        <div className="payments-header-btns">
          <button className="payments-btn-refresh" onClick={load} title="Refresh">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>
          <button className="payments-btn-selfpay" onClick={exportCsv} disabled={filtered.length === 0}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export (CMS-838)
          </button>
          <button className="payments-btn-record" onClick={() => setLogModal(true)} disabled={!canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Log Credit Balance
          </button>
        </div>
      </div>

      {/* Inline notice (replaces native alerts for rescan/match results) */}
      {notice && (
        <div className={`ccb-notice ccb-notice--${notice.type}`}>
          <span>{notice.message}</span>
          <button className="ccb-notice-close" onClick={() => setNotice(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Aging summary cards */}
      {summary && (
        <div className="ccb-summary">
          <div className="ccb-card ccb-card--total">
            <span className="ccb-card-label">Total Open</span>
            <span className="ccb-card-value">{fmtCurrency(summary.total_open)}</span>
            <span className="ccb-card-sub">{summary.count} credit{summary.count === 1 ? '' : 's'}</span>
          </div>
          <div className="ccb-card ccb-card--ok">
            <span className="ccb-card-label">0–30 days</span>
            <span className="ccb-card-value">{fmtCurrency(summary.bucket_0_30)}</span>
          </div>
          <div className="ccb-card ccb-card--warn">
            <span className="ccb-card-label">31–60 days</span>
            <span className="ccb-card-value">{fmtCurrency(summary.bucket_31_60)}</span>
          </div>
          <div className="ccb-card ccb-card--danger">
            <span className="ccb-card-label">60+ days · escalate</span>
            <span className="ccb-card-value">{fmtCurrency(summary.bucket_60_plus)}</span>
          </div>
        </div>
      )}

      {/* Unmatched ERA recoupments (835 PLB takebacks not auto-linked to a credit balance) */}
      {recoups.length > 0 && (
        <div className="ccb-recoup-banner">
          <div className="ccb-recoup-head">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>
              <strong>{recoups.length}</strong> ERA recoupment{recoups.length === 1 ? '' : 's'} from payers couldn’t be auto-matched
              to a credit balance ({fmtCurrency(recoups.reduce((s, r) => s + Number(r.amount - r.applied_amount), 0))}).
            </span>
            <button className="ccb-recoup-link" onClick={() => setRecoupOpen((o) => !o)}>
              {recoupOpen ? 'Hide' : 'Review'}
            </button>
            <button className="ccb-recoup-rescan" onClick={handleRescan} disabled={rescanning || !canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>
              {rescanning ? 'Re-scanning…' : 'Re-scan ERAs'}
            </button>
          </div>

          {recoupOpen && (
            <div className="ccb-recoup-list">
              {matchingAdj && (
                <p className="ccb-recoup-matchhint">
                  Pick a credit balance below to apply this {fmtCurrency(matchingAdj.amount - matchingAdj.applied_amount)} recoupment
                  (PLB {matchingAdj.plb_code}{matchingAdj.reference ? ` · ref ${matchingAdj.reference}` : ''}).
                  <button className="ccb-recoup-link" onClick={() => setMatchingAdj(null)}>Cancel</button>
                </p>
              )}
              {recoups.map((r) => (
                <div key={r.provider_adjustment_id} className="ccb-recoup-row">
                  <span className="ccb-recoup-amt">{fmtCurrency(r.amount - r.applied_amount)}</span>
                  <span className="ccb-recoup-meta">
                    {r.payer_name || 'Unknown payer'} · PLB {r.plb_code}
                    {r.reference ? ` · ref ${r.reference}` : ''}
                    {r.eraid ? ` · ERA ${r.eraid}` : ''}
                  </span>
                  <button
                    className={`ccb-recoup-matchbtn ${matchingAdj?.provider_adjustment_id === r.provider_adjustment_id ? 'active' : ''}`}
                    onClick={() => setMatchingAdj(r)}
                    disabled={!canMove}
                    title={canMove ? undefined : MONEY_ROLE_HINT}
                  >
                    {matchingAdj?.provider_adjustment_id === r.provider_adjustment_id ? 'Selecting…' : 'Match'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="payments-controls">
        <div className="payments-tabs">
          {[
            { key: 'all',   label: 'All ages' },
            { key: '0-30',  label: '0–30' },
            { key: '31-60', label: '31–60' },
            { key: '60+',   label: '60+' },
          ].map((tab) => (
            <button
              key={tab.key}
              className={`payments-tab ${agingFilter === tab.key ? 'active' : ''}`}
              onClick={() => setAgingFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
          <span className="ccb-tab-divider" />
          {[
            { key: 'all',     label: 'Payer & patient' },
            { key: 'payer',   label: 'Payer' },
            { key: 'patient', label: 'Patient' },
          ].map((tab) => (
            <button
              key={`ob-${tab.key}`}
              className={`payments-tab ${obligationFilter === tab.key ? 'active' : ''}`}
              onClick={() => setObligationFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
          <span className="ccb-tab-divider" />
          {[
            { key: 'unresolved', label: 'Unresolved' },
            { key: 'all',        label: 'Include resolved' },
          ].map((tab) => (
            <button
              key={tab.key}
              className={`payments-tab ${statusFilter === tab.key ? 'active' : ''}`}
              onClick={() => setStatusFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="payments-search-wrap">
          <svg className="payments-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="payments-search"
            type="text"
            placeholder="Search claim #, patient, payer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="payments-loading"><div className="payments-spinner" /><span>Loading credit balances…</span></div>
      ) : error ? (
        <div className="payments-error">{error}<button className="payments-error-retry" onClick={load}>Retry</button></div>
      ) : filtered.length === 0 ? (
        <div className="payments-empty">
          <div className="payments-empty-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" /><line x1="12" y1="6" x2="12" y2="8" /><line x1="12" y1="16" x2="12" y2="18" />
            </svg>
          </div>
          <p className="payments-empty-title">
            {search || statusFilter === 'unresolved' ? 'No credit balances to work' : 'No credit balances recorded'}
          </p>
          <p className="payments-empty-sub">Credit balances are logged automatically when a paid claim is rebilled to a different payer.</p>
        </div>
      ) : (
        <div className="payments-table-wrap">
          <table className="payments-table">
            <thead>
              <tr>
                <th>Claim #</th><th>Patient</th><th>Owed to</th><th>Payer</th><th>Source</th>
                <th>Identified</th><th>Due</th><th>Aging</th><th>Originally paid</th><th>Escheat</th>
                <th className="payments-td-amount num">Open Balance</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const bucket = agingBucket(r.aging_days);
                const resolved = TERMINAL_STATUSES.includes(r.status);
                const isPatient = r.obligation_type === 'patient';
                const diligence = dueDiligenceWindow(r);
                const paidMonths = monthsSince(r.original_payment_date);
                const statusLabels = isPatient ? PATIENT_STATUS_LABELS : STATUS_LABELS;
                return (
                  <tr key={r.credit_balance_id} data-obligation={r.obligation_type}>
                    <td className="payments-td-check">{r.claim_number || <span className="ccb-muted">No claim</span>}</td>
                    <td className="payments-td-payer">{r.patient_name || '—'}</td>
                    <td><span className={`ccb-obligation ccb-obligation--${r.obligation_type}`}>{OBLIGATION_LABELS[r.obligation_type] || r.obligation_type}</span></td>
                    <td>{isPatient ? '—' : (r.payer_name || '—')}</td>
                    <td style={{ fontSize: '0.82rem', color: '#6b7280' }}>{SOURCE_LABELS[r.source] || r.source}</td>
                    <td>{fmtDate(r.identified_date)}</td>
                    <td className={r.aging_days > 60 ? 'payments-overdue' : ''}>
                      {fmtDate(r.compliance_due_date)}
                      <span className="ccb-sub">{DUE_BASIS_LABELS[r.due_date_basis] || ''}</span>
                    </td>
                    <td><span className={`ccb-bucket ${bucket.cls}`}>{bucket.label}</span></td>
                    <td>
                      {r.original_payment_date ? fmtDay(r.original_payment_date) : '—'}
                      {!isPatient && r.original_payment_date && (
                        <span
                          className={`ccb-sub ${paidMonths >= 12 ? 'ccb-sub--flag' : ''}`}
                          title={CARRIER_LIMIT_LABEL}
                        >
                          {paidMonths} mo ago · {CARRIER_LIMIT_LABEL}
                        </span>
                      )}
                    </td>
                    <td>
                      {isPatient && r.escheat_due_date ? fmtDay(r.escheat_due_date) : '—'}
                      {diligence && (
                        <span className="ccb-sub ccb-sub--flag">
                          Due diligence {fmtDay(diligence.from)} – {fmtDay(diligence.to)}
                        </span>
                      )}
                    </td>
                    <td className="payments-td-amount" style={{ fontWeight: 600, color: '#991b1b' }}>{fmtCurrency(r.open_balance)}</td>
                    <td><span className={`ccb-status ccb-status--${r.status}`}>{statusLabels[r.status] || r.status}</span></td>
                    <td className="payments-td-actions">
                      {isPatient ? (
                        !resolved && r.claim_id != null && (
                          <button className="payments-action-btn payments-action-resume" onClick={() => setRefundTarget(r)} disabled={!canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>Refund</button>
                        )
                      ) : matchingAdj && !resolved ? (
                        <button className="payments-action-btn payments-action-resume" onClick={() => applyMatch(r)} disabled={!canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>Apply recoupment here</button>
                      ) : !resolved && (
                        <button className="payments-action-btn payments-action-resume" onClick={() => handleResolve(r)} disabled={!canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>Resolve</button>
                      )}
                      {r.claim_id != null && (
                        <button className="payments-action-btn payments-action-view" onClick={() => navigate(`/claims/${r.claim_id}`)}>Claim</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {logModal && (
        <CreditBalanceModal
          onClose={() => setLogModal(false)}
          onSaved={() => { setLogModal(false); load(); }}
        />
      )}

      {refundTarget && (
        <PatientRefundModal
          creditBalance={refundTarget}
          onClose={() => setRefundTarget(null)}
          onRefunded={(data) => {
            setRefundTarget(null);
            setNotice({
              type: 'success',
              message: `Refund of ${fmtCurrency(data?.amount)} recorded${data?.status === 'refund_sent' ? ' — the credit is closed' : ''}.`,
            });
            load();
          }}
        />
      )}

      {resolveTarget && (
        <ResolveCreditBalanceModal
          creditBalance={resolveTarget}
          onClose={() => setResolveTarget(null)}
          onResolved={() => {
            setResolveTarget(null);
            setNotice({ type: 'success', message: 'Credit balance resolution recorded.' });
            load();
          }}
        />
      )}
    </div>
  );
}

/**
 * ⟳ Step 14 (S14-e) — refund a PATIENT credit balance. A refund MOVES money: the server
 * posts a negative `refund` payment on the claim beside the ledger event, so the patient's
 * credit, statement and cash collected all reflect it. Partial refunds compose.
 */
export function PatientRefundModal({ creditBalance, onClose, onRefunded }) {
  const open = (parseFloat(creditBalance.open_balance) || 0).toFixed(2);
  const [amount, setAmount] = useState(open);
  const [reference, setReference] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!reference.trim()) { setError('Enter the check number or EFT trace the refund went out under.'); return; }
    setSaving(true); setError(null);
    try {
      const res = await refundPatientCredit(creditBalance.credit_balance_id, {
        amount, reference: reference.trim(), payment_date: paymentDate,
      });
      onRefunded(res?.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to record the refund.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cpm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form className="cpm-modal" role="dialog" aria-modal="true" aria-label="Refund patient credit" onSubmit={submit}>
        <div className="cpm-header">
          <div>
            <h2 className="cpm-title">Refund Patient Credit</h2>
            <p className="cpm-subtitle">{creditBalance.claim_number} · {creditBalance.patient_name || 'Patient'}</p>
          </div>
          <button type="button" className="cpm-close" onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </div>
        <div className="cpm-body">
          <div className="cpm-mech cpm-mech--rebill" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Open credit owed to the patient</span>
            <strong>{fmtCurrency(open)}</strong>
          </div>
          <p className="cpm-hint">
            Records money leaving: a refund payment is posted against the claim, so the patient&apos;s
            credit, statement and collections all drop by this amount. A partial refund leaves the rest open.
          </p>
          <div className="cpm-field-row">
            <div className="cpm-field">
              <label className="cpm-field-label" htmlFor="ccb-refund-amount">Amount</label>
              <input id="ccb-refund-amount" className="cpm-input" type="number" step="0.01" min="0.01" max={open}
                value={amount} onChange={(e) => setAmount(e.target.value)} required />
            </div>
            <div className="cpm-field">
              <label className="cpm-field-label" htmlFor="ccb-refund-date">Refund date</label>
              <input id="ccb-refund-date" className="cpm-input" type="date"
                value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} required />
            </div>
          </div>
          <div className="cpm-field" style={{ marginTop: 12 }}>
            <label className="cpm-field-label" htmlFor="ccb-refund-ref">Check # or EFT trace</label>
            <input id="ccb-refund-ref" className="cpm-input" maxLength={100}
              value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. CHK 10442" />
          </div>
          {error && <p className="cpm-error" role="alert">{error}</p>}
          <div className="cpm-actions">
            <button type="button" className="cpm-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="cpm-btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Record refund'}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
